import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Users, Mail, Phone, Send, Copy, CheckCircle2, Upload, FileText, X, Download, ShieldCheck } from "lucide-react";
import { DatePicker } from "@/components/app/DatePicker";
import { toast } from "sonner";
import { useRef } from "react";

const TENANT_DOC_TYPES = [
  { value: "id_card",       label: "Pièce d'identité" },
  { value: "work_contract", label: "Contrat de travail" },
  { value: "payslip",       label: "Fiche de paie" },
  { value: "tax_notice",    label: "Avis d'imposition" },
  { value: "residence",     label: "Justificatif de domicile" },
  { value: "guarantor",     label: "Documents du garant" },
  { value: "autre",         label: "Autre" },
];

const empty = {
  first_name: "", last_name: "", email: "", phone: "",
  birth_date: "", profession: "", monthly_income: "",
  guarantor_name: "", guarantor_phone: "", notes: "",
  documents: [],
};

export default function TenantsPage() {
  const { user } = useAuth();
  const { canWrite } = planContext(user);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [inviteFor, setInviteFor] = useState(null);   // {tenant, dev_link}
  const [inviting, setInviting] = useState(null);      // tenant id busy
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("id_card");
  const docInputRef = useRef(null);

  const load = async () => setItems((await api.get("/tenants")).data);
  useEffect(() => { load(); }, []);

  const uploadDoc = async (files) => {
    if (!editId) { toast.error("Enregistrez d'abord le locataire"); return; }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("category", "tenant-documents");
        fd.append("ref_id", editId);
        fd.append("doc_type", docType);
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({
          ...f,
          documents: [...(f.documents || []), {
            id: data.id, type: docType, storage_path: data.storage_path,
            filename: file.name, size: data.size, content_type: data.content_type,
            uploaded_at: data.created_at,
          }],
        }));
      }
      toast.success("Document ajouté");
    } catch (e) { toast.error("Erreur upload : " + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); if (docInputRef.current) docInputRef.current.value = ""; }
  };

  const downloadDoc = async (fileId, filename) => {
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/files/${doc.id}`);
      setForm((f) => ({ ...f, documents: f.documents.filter((d) => d.storage_path !== doc.storage_path) }));
      toast.success("Document supprimé");
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  const sendInvite = async (t) => {
    if (!t.email) { toast.error("Ce locataire n'a pas d'email — renseignez-le d'abord."); return; }
    setInviting(t.id);
    try {
      const { data } = await api.post(`/tenants/${t.id}/invite`);
      setInviteFor({ tenant: t, dev_link: data.dev_link });
      toast.success("Invitation générée");
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
    finally { setInviting(null); }
  };

  const copyLink = async () => {
    if (!inviteFor?.dev_link) return;
    try { await navigator.clipboard.writeText(inviteFor.dev_link); toast.success("Lien copié"); }
    catch { /* fallback */ }
  };

  const openNew = () => { if (!canWrite) return; setForm(empty); setEditId(null); setOpen(true); };

  const viewInsurance = async () => {
    try {
      const res = await api.get(`/tenants/${editId}/insurance/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.target = "_blank"; a.rel = "noopener";
      a.download = form.insurance?.filename || "assurance";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Attestation indisponible"); }
  };
  const openEdit = (t) => {
    setForm({
      ...empty, ...t,
      email: t.email || "",
      phone: t.phone || "",
      birth_date: t.birth_date || "",
      profession: t.profession || "",
      monthly_income: t.monthly_income ?? "",
      guarantor_name: t.guarantor_name || "",
      guarantor_phone: t.guarantor_phone || "",
      notes: t.notes || "",
      documents: Array.isArray(t.documents) ? t.documents : [],
    });
    setEditId(t.id); setOpen(true);
  };

  const save = async () => {
    if (!canWrite) return;
    try {
      const payload = {
        ...form,
        email: form.email || null,
        phone: form.phone || null,
        birth_date: form.birth_date || null,
        profession: form.profession || null,
        monthly_income: form.monthly_income === "" ? null : Number(form.monthly_income),
        guarantor_name: form.guarantor_name || null,
        guarantor_phone: form.guarantor_phone || null,
      };
      if (editId) {
        await api.put(`/tenants/${editId}`, payload);
        if (form.insurance) {
          await api.put(`/tenants/${editId}/insurance-expiry`, { expiry_date: form.insurance.expiry_date || null });
        }
        toast.success("Locataire mis à jour");
      }
      else { await api.post("/tenants", payload); toast.success("Locataire ajouté"); }
      setOpen(false); load();
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!canWrite) return;
    if (!window.confirm("Supprimer ce locataire ?")) return;
    await api.delete(`/tenants/${id}`); toast.success("Supprimé"); load();
  };

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Locataires</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} locataire(s) enregistré(s).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="tenant-new-btn" onClick={openNew} disabled={!canWrite} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              <Plus className="h-4 w-4 mr-2" /> Nouveau locataire
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouveau locataire"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Prénom</Label><Input data-testid="tenant-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><Label>Nom</Label><Input data-testid="tenant-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Date de naissance</Label><DatePicker value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })} placeholder="Date de naissance" className="w-full mt-1" /></div>
              <div><Label>Profession</Label><Input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} /></div>
              <div><Label>Revenus mensuels (€)</Label><Input type="number" value={form.monthly_income} onChange={(e) => setForm({ ...form, monthly_income: e.target.value })} /></div>
              <div className="md:col-span-2 pt-2 border-t border-slate-100 mt-2"><Label className="text-xs uppercase tracking-wider text-slate-500">Garant (optionnel)</Label></div>
              <div><Label>Nom du garant</Label><Input value={form.guarantor_name} onChange={(e) => setForm({ ...form, guarantor_name: e.target.value })} /></div>
              <div><Label>Téléphone garant</Label><Input value={form.guarantor_phone} onChange={(e) => setForm({ ...form, guarantor_phone: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Notes internes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="md:col-span-2 border-t border-slate-100 pt-4">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Documents ({(form.documents || []).length})</Label>
                {editId ? (
                  <>
                    <div className="mt-2 flex items-center gap-2">
                      <select value={docType} onChange={(e) => setDocType(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-sm" data-testid="tenant-doc-type">
                        {TENANT_DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <label className="flex items-center gap-1 text-xs bg-teal-50 border border-teal-200 hover:bg-teal-100 rounded px-3 py-1.5 cursor-pointer" data-testid="tenant-doc-upload">
                        <Upload className="h-3.5 w-3.5" /> {uploading ? "Envoi…" : "Ajouter un document"}
                        <input ref={docInputRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => uploadDoc(e.target.files)} />
                      </label>
                    </div>
                    <div className="mt-3 space-y-1">
                      {(form.documents || []).map((d) => (
                        <div key={d.storage_path} className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid={`tenant-doc-row-${d.id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                            <div className="min-w-0">
                              <div className="font-medium truncate">{d.filename}</div>
                              <div className="text-slate-500 text-[10px]">{TENANT_DOC_TYPES.find((t) => t.value === d.type)?.label || d.type} · {Math.round((d.size || 0) / 1024)} Ko</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {d.id && <Button variant="ghost" size="icon" onClick={() => downloadDoc(d.id, d.filename)}><Download className="h-3.5 w-3.5" /></Button>}
                            <Button variant="ghost" size="icon" onClick={() => deleteDoc(d)}><X className="h-3.5 w-3.5 text-rose-600" /></Button>
                          </div>
                        </div>
                      ))}
                      {(form.documents || []).length === 0 && (
                        <p className="text-xs text-slate-400">Aucun document. Ajoutez la pièce d'identité, le contrat de travail, les 3 dernières fiches de paie…</p>
                      )}
                    </div>
                    <p className="mt-2 text-[10px] text-slate-400">Conservation RGPD : les documents sont stockés de manière chiffrée. Supprimez-les à la fin du bail (obligation légale : au plus 3 mois après le départ du locataire, sauf litige).</p>
                  </>
                ) : (
                  <p className="text-xs text-slate-400 mt-2">Enregistrez d'abord le locataire pour joindre ses documents.</p>
                )}
              </div>
              {editId && (
                <div className="md:col-span-2 border-t border-slate-100 pt-4">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-teal-700" /> Assurance habitation
                  </Label>
                  {form.insurance ? (
                    <div className="mt-2 space-y-2" data-testid="tenant-insurance-row">
                      <div className="flex items-center justify-between gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                          <div className="font-medium truncate">{form.insurance.filename}</div>
                        </div>
                        <Button data-testid="tenant-insurance-view" variant="ghost" size="icon" onClick={viewInsurance}><Download className="h-3.5 w-3.5" /></Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] text-slate-500 whitespace-nowrap">Date d'échéance</Label>
                        <DatePicker
                          data-testid="tenant-insurance-expiry"
                          value={form.insurance.expiry_date ? String(form.insurance.expiry_date).slice(0, 10) : ""}
                          onChange={(v) => setForm((f) => ({ ...f, insurance: { ...f.insurance, expiry_date: v || null } }))}
                          placeholder="Choisir une date"
                          className="h-8 text-xs"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400">Cliquez sur « Enregistrer » en bas pour conserver la date.</p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 mt-2">Le locataire n'a pas encore déposé d'attestation depuis son espace locataire.</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button data-testid="tenant-save" onClick={save} className="bg-[hsl(var(--primary))]">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-10 w-10 mx-auto text-slate-300" />
            <div className="mt-3 text-sm text-slate-500">Aucun locataire enregistré.</div>
          </div>
        ) : (
          <>
          <div className="hidden sm:block">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Locataire</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Profession</TableHead>
                <TableHead>Revenus</TableHead>
                <TableHead>Garant</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`tenant-row-${t.id}`}>
                  <TableCell><div className="font-medium">{t.first_name} {t.last_name}</div></TableCell>
                  <TableCell>
                    <div className="text-xs text-slate-600 flex items-center gap-1"><Mail className="h-3 w-3" />{t.email || "—"}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />{t.phone || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm">{t.profession || "—"}</TableCell>
                  <TableCell className="text-sm">{t.monthly_income ? fmtEUR(t.monthly_income) : "—"}</TableCell>
                  <TableCell className="text-sm">{t.guarantor_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`tenant-invite-${t.id}`}
                      title={t.portal_activated_at ? "Portail activé" : "Envoyer l'accès au portail"}
                      disabled={inviting === t.id}
                      onClick={() => sendInvite(t)}
                    >
                      {t.portal_activated_at
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        : <Send className="h-4 w-4 text-teal-700" />}
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`tenant-edit-${t.id}`} disabled={!canWrite} onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`tenant-delete-${t.id}`} disabled={!canWrite} onClick={() => remove(t.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>

          {/* Vue mobile : cartes empilées */}
          <div className="sm:hidden divide-y divide-slate-100">
            {items.map((t) => (
              <div key={t.id} className="p-4" data-testid={`tenant-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">{t.first_name} {t.last_name}</div>
                    {t.profession && <div className="text-xs text-slate-500">{t.profession}</div>}
                  </div>
                  <Button
                    variant="ghost" size="icon" className="shrink-0"
                    data-testid={`tenant-invite-m-${t.id}`}
                    title={t.portal_activated_at ? "Portail activé" : "Envoyer l'accès au portail"}
                    disabled={inviting === t.id}
                    onClick={() => sendInvite(t)}
                  >
                    {t.portal_activated_at
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : <Send className="h-4 w-4 text-teal-700" />}
                  </Button>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-y-1 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{t.email || "—"}</span></div>
                  <div className="flex items-center gap-1.5 text-slate-600"><Phone className="h-3.5 w-3.5 shrink-0" />{t.phone || "—"}</div>
                  <div><span className="text-slate-500">Revenus : </span>{t.monthly_income ? fmtEUR(t.monthly_income) : "—"}</div>
                  <div><span className="text-slate-500">Garant : </span>{t.guarantor_name || "—"}</div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button variant="outline" size="sm" className="flex-1" disabled={!canWrite} onClick={() => openEdit(t)} data-testid={`tenant-edit-m-${t.id}`}><Pencil className="h-4 w-4 mr-1" /> Modifier</Button>
                  <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => remove(t.id)} data-testid={`tenant-delete-m-${t.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>

      {/* Dialog Invitation Portail */}
      <Dialog open={!!inviteFor} onOpenChange={(o) => !o && setInviteFor(null)}>
        <DialogContent data-testid="tenant-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invitation au portail locataire</DialogTitle>
            <DialogDescription>Envoyez à votre locataire un lien d'activation à usage unique valable 24 h.</DialogDescription>
          </DialogHeader>
          {inviteFor && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Un lien d'activation a été généré pour <b>{inviteFor.tenant.first_name} {inviteFor.tenant.last_name}</b> (<i>{inviteFor.tenant.email}</i>).
              </p>
              {inviteFor.dev_link ? (
                <>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="invite-dev-link-box">
                    <div className="text-xs font-semibold text-amber-900 mb-1">🛠️ Mode dev — Copiez ce lien et envoyez-le au locataire :</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs text-teal-700 break-all bg-white px-2 py-1 rounded border border-amber-200" data-testid="invite-dev-link">{inviteFor.dev_link}</code>
                      <Button size="sm" variant="outline" onClick={copyLink} data-testid="invite-copy-btn">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    En production, ce lien sera envoyé automatiquement par email (intégration Resend à venir).
                  </p>
                </>
              ) : (
                <p className="text-sm text-emerald-700">Email d'invitation envoyé.</p>
              )}
              <div className="flex justify-end">
                <Button onClick={() => { setInviteFor(null); load(); }} className="bg-[hsl(var(--primary))]">Fermer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
